import React, { useState, useEffect, useCallback } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { AccessTime as TimeIcon } from '@mui/icons-material';
import { useAppSelector } from '../../hooks/store-hooks';
import { useAuthService } from '../../../use-cases/auth/useAuthService';

export const SessionTimer = () => {
  const { logout } = useAuthService();
  const { sessionTimeoutSeconds, loginTimestamp, isValid } = useAppSelector((state) => state.auth);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  const calculateTimeLeft = useCallback(() => {
    if (!loginTimestamp) return null;
    
    const now = Date.now();
    const expirationTime = loginTimestamp + (sessionTimeoutSeconds * 1000);
    const difference = expirationTime - now;
    
    return Math.max(0, Math.floor(difference / 1000));
  }, [loginTimestamp, sessionTimeoutSeconds]);

  useEffect(() => {
    if (!isValid || !loginTimestamp) return;

    const initial = calculateTimeLeft();
    setTimeLeft(initial);

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining !== null && remaining <= 0) {
        clearInterval(timer);
        logout();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isValid, loginTimestamp, calculateTimeLeft, logout]);

  if (timeLeft === null || !isValid) return null;

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}h ${mins.toString().padStart(2, '0')}m`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isLowTime = timeLeft < 300; // 5 minutos

  return (
    <Tooltip title="Tempo restante de sessão antes do logout automático" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={<TimeIcon sx={{ fontSize: '1rem !important', color: isLowTime ? '#ff1744' : '#ee9338' }} />}
          label={formatTime(timeLeft)}
          size="small"
          sx={{
            fontWeight: 800,
            fontFamily: 'monospace',
            color: isLowTime ? '#ff1744' : '#ee9338',
            backgroundColor: '#ffffff',
            border: '1.5px solid',
            borderColor: isLowTime ? '#ff1744' : '#ee9338',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            px: 0.5,
            transition: 'all 0.3s ease',
            '& .MuiChip-label': { px: 1 },
            animation: isLowTime ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.7 },
              '100%': { opacity: 1 },
            }
          }}
        />
      </Box>
    </Tooltip>
  );
};
